'use es6';

import { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { DropTarget } from 'react-dnd-compat';
import { dropNewImageElements, dropNewVideoElements } from 'LayoutBuilderUI/actions/layoutActions';
import { getIsFileManagerOpen } from 'LayoutBuilderUI/selectors/userInterface';
import { SnapPlaceholder } from 'LayoutBuilderUI/components/dragAndDrop/SnapPlaceholder';
import { ELEMENT, ELEMENT_PLACEHOLDER, SECTION, LAYOUT } from 'LayoutBuilderUI/lib/DragDropConstants';
import withDropComponent from 'LayoutBuilderUI/hoc/withDropComponent';
import { getDropPositionForColumn } from 'LayoutBuilderUI/utils/DragDropUtils';
import { STACK, VERTICAL_ALIGN } from 'EmailEditorUI/constants/SectionLayoutConstants';
import { NativeTypes } from 'react-dnd-ipeui-backend';
import { DropPosition } from 'EmailEditorUI/redux/actions/types/layoutActions';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const widgetDragTarget = {
  drop(props, monitor) {
    if (monitor.didDrop()) {
      return undefined;
    }
    const {
      sectionIndex,
      index,
      firstElementId,
      lastElementId,
      numElements,
      id,
      sectionId,
      verticalAlign
    } = props;
    let dropProps = {};
    const item = monitor.getItem();
    if (numElements === 0) {
      dropProps = {
        type: ELEMENT_PLACEHOLDER,
        sectionId,
        columnId: id
      };
    } else {
      const dropPosition = getDropPositionForColumn(verticalAlign, props.getRect(), monitor.getClientOffset());
      const elementId = dropPosition === DropPosition.BEFORE ? firstElementId : lastElementId;
      dropProps = {
        type: ELEMENT,
        sectionIndex,
        columnIndex: index,
        elementIndex: numElements - 1,
        elementId,
        dropPosition,
        isVertical: false
      };
    }
    if (monitor.getItemType() === NativeTypes.FILE) {
      if (item.files && item.files.length) {
        const imageFiles = item.files.filter(f => f.type.startsWith('image'));
        const videoFiles = item.files.filter(f => f.type.startsWith('video'));
        if (imageFiles.length) {
          props.dropNewImageElements(Object.assign({
            files: imageFiles
          }, dropProps));
        }
        if (videoFiles.length) {
          props.dropNewVideoElements(Object.assign({
            files: videoFiles
          }, dropProps));
        }
      }
      return undefined;
    }
    return dropProps;
  },
  hover(props, monitor) {
    const {
      verticalAlign
    } = props;
    const dropPosition = getDropPositionForColumn(verticalAlign, props.getRect(), monitor.getClientOffset());
    props.updateSnapPlaceholderPosition(dropPosition);
  }
};
class ColumnDOM extends Component {
  constructor(...args) {
    super(...args);
    this.getSnapPlaceholderRef = ref => {
      this.snapPlaceholderRef = ref;
    };
  }
  componentDidUpdate(prevProps) {
    if (!prevProps.isOver && this.props.isOver && this.snapPlaceholderRef) {
      const rect = this.snapPlaceholderRef.getBoundingClientRect();
      if (rect.top < 0) {
        this.snapPlaceholderRef.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }
    }
  }
  render() {
    const {
      children,
      id,
      width,
      canResize,
      numColumns,
      isOver,
      connectDropTarget,
      isOverChildElement,
      numElements,
      stack,
      verticalAlign,
      snapPlaceholderPosition,
      getElementRef,
      onDragEnter
    } = this.props;
    const classes = classNames('hse-column', canResize ? `hse-size-${width} position-relative` : numColumns === 2 && [width === 4 && 'hse-narrow', width === 8 && 'hse-wide'], stack === STACK.NONE && 'hse-no-stack', verticalAlign !== VERTICAL_ALIGN.TOP && `valign-${verticalAlign}`.toLowerCase());
    const content =
    /*#__PURE__*/
    // @bend-expect trellis-tokenized
    _jsxs("div", {
      id: id,
      className: classes,
      ref: getElementRef,
      onDragEnter: onDragEnter,
      children: [children, canResize && numElements > 0 && /*#__PURE__*/_jsx(SnapPlaceholder, {
        isVisible: isOver,
        getRef: this.getSnapPlaceholderRef,
        position: snapPlaceholderPosition
      })]
    });
    if (canResize && !isOverChildElement) {
      return connectDropTarget(content);
    }
    return content;
  }
}
ColumnDOM.propTypes = {
  canResize: PropTypes.bool,
  children: PropTypes.node,
  id: PropTypes.string,
  width: PropTypes.number,
  numColumns: PropTypes.number.isRequired,
  isOver: PropTypes.bool.isRequired,
  isOverChildElement: PropTypes.bool,
  numElements: PropTypes.number,
  stack: PropTypes.string,
  verticalAlign: PropTypes.oneOf(Object.keys(VERTICAL_ALIGN)).isRequired,
  connectDropTarget: PropTypes.func,
  // withDropComponent props
  onDragEnter: PropTypes.func,
  getElementRef: PropTypes.func,
  snapPlaceholderPosition: PropTypes.string
};
const DropTargetColumn = compose(connect(state => ({
  isFileManagerOpen: getIsFileManagerOpen(state)
}), {
  dropNewImageElements,
  dropNewVideoElements
}), withDropComponent, DropTarget([ELEMENT, NativeTypes.FILE], widgetDragTarget, monitor => {
  return {
    isHoveringFile: monitor.getItemType() === NativeTypes.FILE && monitor.isOver(),
    isOver: monitor.isOver({
      shallow: monitor.getItemType() !== SECTION && monitor.getItemType() !== LAYOUT
    })
  };
}))(ColumnDOM);
export default DropTargetColumn;